import { WorkLineModel, WorkOrderObjectModel } from './work-order-object.model';

export type WorkLineStatus = 'notStarted' | 'inProgress' | 'completed';

export interface WorkLineFilters {
  search: string;
  progressType: string;
  status: '' | WorkLineStatus;
  workType: string;
}

export function createEmptyWorkLineFilters(): WorkLineFilters {
  return { search: '', progressType: '', status: '', workType: '' };
}

export function hasActiveWorkLineFilters(f: WorkLineFilters | undefined): boolean {
  return !!f && (!!f.search.trim() || !!f.progressType || !!f.status || !!f.workType);
}

export function getWorkLineStatus(wl: WorkLineModel): WorkLineStatus {
  const progress = wl.progress ?? 0;
  if (wl.completed || progress >= 100) {
    return 'completed';
  }
  return progress > 0 ? 'inProgress' : 'notStarted';
}

function includes(value: string | undefined, term: string): boolean {
  return (value ?? '').toLowerCase().includes(term);
}

function objectMatchesText(obj: WorkOrderObjectModel, term: string): boolean {
  const cable = obj.cable;
  return (
    includes(obj.objectNumber, term) ||
    includes(cable?.fromTagNo, term) ||
    includes(cable?.fromTagName, term) ||
    includes(cable?.toTagNo, term) ||
    includes(cable?.toTagName, term)
  );
}

function workLineMatchesFields(wl: WorkLineModel, f: WorkLineFilters): boolean {
  if (f.progressType && wl.progressType !== f.progressType) {
    return false;
  }
  if (f.status && getWorkLineStatus(wl) !== f.status) {
    return false;
  }
  return !f.workType || wl.workType === f.workType;
}

function workLineMatchesText(wl: WorkLineModel, term: string): boolean {
  return includes(wl.description, term) || includes(wl.name, term) || includes(wl.title, term);
}

export function getVisibleWorkLines(obj: WorkOrderObjectModel, f: WorkLineFilters | undefined): WorkLineModel[] {
  const lines = obj.workLines ?? [];
  if (!hasActiveWorkLineFilters(f)) {
    return lines;
  }
  const term = f.search.trim().toLowerCase();
  const objectHit = !!term && objectMatchesText(obj, term);
  return lines.filter((wl) => workLineMatchesFields(wl, f) && (!term || objectHit || workLineMatchesText(wl, term)));
}

export function objectHasMatch(obj: WorkOrderObjectModel, f: WorkLineFilters | undefined): boolean {
  if (!hasActiveWorkLineFilters(f)) {
    return true;
  }
  return getVisibleWorkLines(obj, f).length > 0 || (obj.children ?? []).some((c) => objectHasMatch(c, f));
}

function collectDistinct(
  objects: WorkOrderObjectModel[] | undefined,
  pick: (wl: WorkLineModel) => string | undefined,
): string[] {
  const values = new Set<string>();
  const visit = (o: WorkOrderObjectModel) => {
    (o.workLines ?? []).forEach((wl) => {
      const value = pick(wl);
      if (value) {
        values.add(value);
      }
    });
    (o.children ?? []).forEach(visit);
  };
  (objects ?? []).forEach(visit);
  return Array.from(values).sort();
}

export function collectWorkTypes(objects: WorkOrderObjectModel[] | undefined): string[] {
  return collectDistinct(objects, (wl) => wl.workType);
}

export function collectProgressTypes(objects: WorkOrderObjectModel[] | undefined): string[] {
  return collectDistinct(objects, (wl) => wl.progressType);
}
